/**
 * Authentication Store
 * Manages LDA authentication state with session-based authentication.
 * SECURITY: Uses httpOnly cookies for session management (handled by backend).
 * @packageDocumentation
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import i18n from '@/i18n';
import { authApi, AuthApiError } from '@/api/auth';
import { NEXUS_LOGOUT_CHANNEL } from '@/lib/popup';

// =============================================================================
// Error extraction helpers
// =============================================================================

/**
 * Extract error code from backend error response.
 * Handles both `{ error: { code } }` and `{ error: "CODE" }` shapes.
 *
 * @param { Record<string, unknown> } errorData The error response data.
 * @param { string } fallback The fallback error code if none is found.
 * @returns { string } The extracted error code, or the fallback if not found.
 */
const extractErrorCode = (errorData: Record<string, unknown>, fallback: string): string => {
  const err = errorData.error;

  if (typeof err === 'object' && err !== null && 'code' in err) {
    return String((err as { code: unknown }).code);
  }

  if (typeof err === 'string') {
    return err;
  }

  return fallback;
};

/**
 * Extract error message from backend error response.
 * Handles both `{ error: { message } }` and `{ message: "..." }` shapes.
 *
 * @param { Record<string, unknown> } errorData The error response data.
 * @param { string } fallback The fallback error message if none is found.
 * @returns { string } The extracted error message, or the fallback if not found.
 */
const extractErrorMessage = (errorData: Record<string, unknown>, fallback: string): string => {
  const err = errorData.error;

  if (typeof err === 'object' && err !== null && 'message' in err) {
    return String((err as { message: unknown }).message);
  }

  if (typeof errorData.message === 'string') {
    return errorData.message;
  }

  return fallback;
};

/**
 * User role types for LDA integration.
 * Role mapping from LDA UserRole enum.
 *   - admin: LDA Admin ('admin') - full access
 *   - editor: LDA Standard ('standard') - read/write
 *   - viewer: LDA ReadOnly ('read_only') - read only
 */
export type AuthUserRole = 'admin' | 'editor' | 'viewer';

/**
 * Authentication provider type.
 */
export type AuthProvider = 'lda' | 'google';

/**
 * LDA retailer information.
 */
export interface AuthRetailer {
  id: string;
  name: string;
  code: string;
}

/**
 * Authenticated user information from LDA.
 * `authProvider` is optional because the login endpoint does not return it —
 * it is only present in the /me (current-user) endpoint response.
 */
export interface AuthUser {
  id: string;
  email: string;
  lastName?: string;
  role: AuthUserRole;
  firstName?: string;
  authProvider?: AuthProvider;
}

/**
 * Authentication error information.
 */
export interface AuthError {
  code: string;
  message: string;
}

/**
 * Login request payload for LDA authentication.
 */
export interface LoginRequest {
  username: string;
  password: string;
}

/**
 * Re-authentication request payload (password only).
 */
export interface ReauthRequest {
  password: string;
}

/**
 * Login response from the API.
 */
export interface LoginResponse {
  user: AuthUser;
  retailers: AuthRetailer[];
}

/**
 * Authentication state interface.
 */
interface AuthState {
  isLoading: boolean;
  user: AuthUser | null;
  loginAt: number | null;
  isInitializing: boolean;
  error: AuthError | null;
  sessionExpired: boolean;
  ldaUnavailable: boolean;
  isAuthenticated: boolean;
  retailers: AuthRetailer[];
  googleClientId: string | null;
  sessionTtlHours: number | null;
}

/**
 * Authentication actions interface.
 */
interface AuthActions {
  reset: () => void;
  clearError: () => void;
  setGoogleError: () => void;
  logout: () => Promise<void>;
  markSessionExpired: () => void;
  clearSessionExpired: () => void;
  setLoginAt: (ts: number) => void;
  checkSession: () => Promise<void>;
  keepAlive: () => Promise<boolean>;
  loadAuthConfig: () => Promise<void>;
  initializeSession: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
  setSessionTtlHours: (hours: number) => void;
  login: (request: LoginRequest) => Promise<void>;
  setLdaUnavailable: (unavailable: boolean) => void;
  reauth: (request: ReauthRequest) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  error: null,
  retailers: [],
  loginAt: null,
  isLoading: false,
  isInitializing: true,
  googleClientId: null,
  sessionExpired: false,
  ldaUnavailable: false,
  sessionTtlHours: null,
  isAuthenticated: false,
};

/**
 * Authentication store for LDA/Google SSO authentication.
 * Uses Zustand with persist middleware.
 * Only persists minimal user info for session restoration.
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      clearError: () => {
        set({
          error: null,
        });
      },

      setLoginAt: (ts: number) => {
        set({
          loginAt: ts,
        });
      },

      setUser: (user: AuthUser | null) => {
        set({
          user,
        });
      },

      clearSessionExpired: () => {
        set({
          sessionExpired: false,
        });
      },

      setSessionTtlHours: (hours: number) => {
        set({
          sessionTtlHours: hours,
        });
      },

      setLdaUnavailable: (unavailable: boolean) => {
        set({
          ldaUnavailable: unavailable,
        });
      },

      reset: () => {
        set({
          ...initialState,
          isLoading: false,
          isInitializing: false,
        });
      },

      markSessionExpired: () => {
        if (!get().sessionExpired) {
          set({
            sessionExpired: true,
          });
        }
      },

      setGoogleError: () => {
        set({
          error: {
            code: 'GOOGLE_LOGIN_FAILED',
            message: i18n.t('pages:auth.login.googleFailed'),
          },
        });
      },

      logout: async () => {
        // Notify any open popup windows (e.g., pipeline logs) to close
        const bc = new BroadcastChannel(NEXUS_LOGOUT_CHANNEL);
        bc.postMessage('logout');
        bc.close();

        try {
          await fetch('/api/auth/logout', {
            method: 'POST',
            credentials: 'include',
          });
        } finally {
          set({
            ...initialState,
            isLoading: false,
            isInitializing: false,
          });
        }
      },

      loadAuthConfig: async (): Promise<void> => {
        try {
          const data = await authApi.config();

          const update: Partial<AuthState> = {};

          if (typeof data.sessionTtlHours === 'number') {
            update.sessionTtlHours = data.sessionTtlHours;
          }

          if (data.googleAuth) {
            update.googleClientId = data.googleAuth.clientId ?? null;
          }

          set(update);
        } catch {
          // Non-critical — lifecycle will still work based on client clock
        }
      },

      keepAlive: async (): Promise<boolean> => {
        try {
          const response = await fetch('/api/auth/me', {
            credentials: 'include',
          });

          if (response.ok) {
            set({
              loginAt: Date.now(),
            });

            return true;
          }

          if (response.status === 401) {
            const errorData: Record<string, unknown> = await response.json().catch((): Record<string, unknown> => ({}));
            const errorCode = extractErrorCode(errorData, '');
            const isExpired = errorCode === 'SESSION_EXPIRED';

            if (isExpired) {
              set({
                sessionExpired: true,
              });
            }
          }

          return false;
        } catch {
          return false;
        }
      },

      checkSession: async () => {
        set({
          isLoading: true,
        });

        try {
          const response = await fetch('/api/auth/me', {
            credentials: 'include',
          });

          if (response.ok) {
            const data = await response.json();
            set({
              isLoading: false,
              isAuthenticated: true,
              sessionExpired: false,
              user: data.user ?? data,
              retailers: data.retailers ?? [],
            });
          } else if (response.status === 401) {
            const errorData: Record<string, unknown> = await response.json().catch((): Record<string, unknown> => ({}));
            const errorCode = extractErrorCode(errorData, '');
            const isExpired = errorCode === 'SESSION_EXPIRED';

            if (isExpired) {
              set({
                isLoading: false,
                sessionExpired: true,
              });
            } else {
              set({
                user: null,
                retailers: [],
                isLoading: false,
                sessionExpired: false,
                isAuthenticated: false,
              });
            }
          } else {
            set({
              user: null,
              retailers: [],
              isLoading: false,
              isAuthenticated: false,
            });
          }
        } catch {
          set({
            user: null,
            retailers: [],
            isLoading: false,
            isAuthenticated: false,
          });
        }
      },

      initializeSession: async () => {
        set({
          isInitializing: true,
        });

        try {
          const response = await fetch('/api/auth/me', {
            credentials: 'include',
          });

          if (response.ok) {
            const data = await response.json();
            set({
              user: data.user,
              isLoading: false,
              loginAt: Date.now(),
              isAuthenticated: true,
              isInitializing: false,
              retailers: data.retailers ?? [],
            });
          } else {
            const errorData: Record<string, unknown> = await response.json().catch((): Record<string, unknown> => ({}));
            const errorCode = extractErrorCode(errorData, '');
            const isExpired = errorCode === 'SESSION_EXPIRED';

            if (isExpired) {
              // Preserve user/retailers so the SessionExpiredModal can show the email
              set({
                isLoading: false,
                sessionExpired: true,
                isInitializing: false,
              });
            } else {
              set({
                user: null,
                retailers: [],
                isLoading: false,
                isInitializing: false,
                sessionExpired: false,
                isAuthenticated: false,
              });
            }
          }
        } catch {
          set({
            user: null,
            retailers: [],
            isLoading: false,
            isInitializing: false,
            isAuthenticated: false,
          });
        }
      },

      loginWithGoogle: async (credential: string) => {
        set({
          error: null,
          isLoading: true,
          ldaUnavailable: false,
        });

        try {
          const data = await authApi.googleVerify(credential);

          set({
            error: null,
            user: data.user,
            isLoading: false,
            loginAt: Date.now(),
            isAuthenticated: true,
            sessionExpired: false,
            sessionTtlHours: null,
            retailers: data.retailers ?? [],
          });
        } catch (error) {
          if (error instanceof AuthApiError) {
            const errorCode = error.code || 'GOOGLE_LOGIN_FAILED';
            const errorMessage = error.message || i18n.t('pages:auth.login.googleFailed');

            if (error.status === 503 || errorCode === 'LDA_UNAVAILABLE') {
              set({
                isLoading: false,
                ldaUnavailable: true,
                error: {
                  code: errorCode,
                  message: errorMessage,
                },
              });
            } else {
              set({
                isLoading: false,
                error: {
                  code: errorCode,
                  message: errorMessage,
                },
              });
            }
          } else if (!get().error) {
            set({
              isLoading: false,
              error: {
                code: 'GOOGLE_LOGIN_FAILED',
                message: error instanceof Error ? error.message : i18n.t('pages:auth.login.googleFailedGeneric'),
              },
            });
          }

          throw error;
        }
      },

      reauth: async (request: ReauthRequest) => {
        set({
          error: null,
          isLoading: true,
        });

        try {
          const response = await fetch('/api/auth/reauth', {
            method: 'POST',
            credentials: 'include',
            body: JSON.stringify(request),
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({
              error: 'REAUTH_FAILED',
              message: i18n.t('pages:auth.login.reauthFailed'),
            }));

            const errorCode = extractErrorCode(errorData, 'REAUTH_FAILED');
            const errorMessage = extractErrorMessage(errorData, i18n.t('pages:auth.login.invalidPassword'));

            set({
              isLoading: false,
              error: {
                code: errorCode,
                message: errorMessage,
              },
            });

            throw new Error(errorMessage);
          }

          const data = (await response.json()) as LoginResponse;

          set({
            error: null,
            user: data.user,
            isLoading: false,
            loginAt: Date.now(),
            isAuthenticated: true,
            sessionExpired: false,
            sessionTtlHours: null,
            retailers: data.retailers ?? [],
          });
        } catch (error) {
          if (!get().error) {
            set({
              isLoading: false,
              error: {
                code: 'REAUTH_FAILED',
                message: error instanceof Error ? error.message : i18n.t('pages:auth.login.reauthFailed'),
              },
            });
          }

          throw error;
        }
      },

      login: async (request: LoginRequest) => {
        set({
          error: null,
          isLoading: true,
          ldaUnavailable: false,
        });

        try {
          const response = await fetch('/api/auth/login', {
            method: 'POST',
            credentials: 'include',
            body: JSON.stringify(request),
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({
              error: 'LOGIN_FAILED',
              message: i18n.t('pages:auth.login.loginFailed'),
            }));

            const errorCode = extractErrorCode(errorData, 'LOGIN_FAILED');
            const errorMessage = extractErrorMessage(errorData, i18n.t('pages:auth.login.loginFailedDefault'));

            if (errorCode === 'LDA_UNAVAILABLE') {
              set({
                isLoading: false,
                ldaUnavailable: true,
                error: {
                  code: errorCode,
                  message: errorMessage,
                },
              });
            } else {
              set({
                isLoading: false,
                error: {
                  code: errorCode,
                  message: errorMessage,
                },
              });
            }

            throw new Error(errorMessage);
          }

          const data = (await response.json()) as LoginResponse;

          set({
            error: null,
            user: data.user,
            isLoading: false,
            loginAt: Date.now(),
            isAuthenticated: true,
            sessionExpired: false,
            sessionTtlHours: null,
            retailers: data.retailers ?? [],
          });
        } catch (error) {
          if (!get().error) {
            set({
              isLoading: false,
              error: {
                code: 'LOGIN_FAILED',
                message: error instanceof Error ? error.message : i18n.t('pages:auth.login.loginFailed'),
              },
            });
          }

          throw error;
        }
      },
    }),
    {
      name: 'nexus-auth',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        user: state.user,
        loginAt: state.loginAt,
        googleClientId: state.googleClientId,
        sessionTtlHours: state.sessionTtlHours,
      }),
    },
  ),
);
